import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, 
  BarChart3, 
  Settings, 
  Target, 
  Brain, 
  Heart, 
  Lock, 
  MessageSquare,
  ArrowRight,
  Briefcase,
  Clock,
  Users,
  FileCheck
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const values = [
  { icon: Shield, title: 'Stewardship & Integrity', desc: 'We protect resources, data, and trust.' },
  { icon: BarChart3, title: 'Evidence-Based Decisions', desc: 'We validate before we recommend.' },
  { icon: Settings, title: 'Practical Implementation', desc: "If it can't be implemented, it doesn't belong in the plan." },
  { icon: Target, title: 'Accountability for Outcomes', desc: 'Define success, track it, report it.' },
  { icon: Brain, title: 'Governance First', desc: 'Especially for AI/LLMs, data, and enterprise systems.' },
  { icon: Heart, title: 'Respect for People & Change', desc: 'Adoption is the work, not an afterthought.' },
  { icon: Lock, title: 'Security & Privacy by Design', desc: "Reduce risk; don't create exposure." },
  { icon: MessageSquare, title: 'Clear Communication', desc: 'Leaders need clarity, not jargon.' },
];

const engagementModels = [
  { icon: Briefcase, title: 'Project-Based Delivery', desc: 'Defined scope and deliverables' },
  { icon: Clock, title: 'Advisory Retainer', desc: 'Ongoing support and oversight' },
  { icon: Users, title: 'Staff Augmentation', desc: 'Embedded expertise and surge support' },
  { icon: FileCheck, title: 'PMO / Program Delivery', desc: 'Execution, reporting, governance' },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 bg-radial-top" />
        <div className="absolute top-1/3 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl sm:text-6xl font-bold mb-6">
              About <span className="text-gradient">ANJO</span>
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              A professional services firm supporting private, public, quasi-government, and all other organizations with management consulting, finance advisory, operational efficiency, and technology transformation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl glass glow-border"
              data-testid="mission-block"
            >
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 mb-4">
                Mission
              </div>
              <p className="text-lg text-foreground leading-relaxed">
                To help private, public, and quasi organizations improve performance through practical strategy, disciplined financial management, operational efficiency, and responsible AI governance.
              </p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl glass glow-border"
              data-testid="vision-block"
            >
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20 mb-4">
                Vision
              </div>
              <p className="text-lg text-foreground leading-relaxed">
                A world where organizations achieve better outcomes with less waste—using trustworthy data, disciplined operations, and technology that is governed, secure, and fit-for-purpose.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 relative bg-grid-fine" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl sm:text-5xl font-bold mb-12"
          >
            Our <span className="text-gradient">Approach</span>
          </motion.h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                We bring a disciplined, governance-first approach to complex work—strategy execution, performance measurement, enterprise risk management, data readiness, and responsible AI adoption.
              </p>
              <ul className="space-y-4">
                {[
                  'Clear decision rights and accountable ownership',
                  'Controls and policy that match real operations',
                  'Data that is validated and fit-for-purpose',
                  'Delivery discipline that survives leadership turnover',
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ArrowRight className="text-white" size={12} />
                    </div>
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl glass"
            >
              <blockquote className="text-xl text-muted-foreground italic leading-relaxed">
                &ldquo;If you&apos;ve ever said, &lsquo;We&apos;ve tried this before,&rsquo; you&apos;re not wrong. Usually the difference isn&apos;t the idea—it&apos;s governance and execution.&rdquo;
              </blockquote>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              Core <span className="text-gradient">Values</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>
          
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="p-6 rounded-2xl glass hover:bg-secondary/50 transition-all duration-300 group"
                data-testid={`value-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center mb-4 group-hover:from-cyan-500/30 group-hover:to-purple-500/30 transition-all">
                  <value.icon className="text-cyan-400" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{value.title}</h3>
                <p className="text-muted-foreground text-sm">{value.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="py-20 relative" data-testid="engagement-section">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-purple-500/5 to-cyan-500/5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              Engagement <span className="text-gradient">Models</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Flexible approaches to meet your organization&apos;s needs
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {engagementModels.map((model, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl glass glow-border text-center"
                data-testid={`engagement-card-${index}`}
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center mb-4">
                  <model.icon className="text-white" size={28} />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">{model.title}</h3>
                <p className="text-muted-foreground text-sm">{model.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
            Let&apos;s Work <span className="text-gradient">Together</span>
          </h2>
          <p className="text-xl text-muted-foreground mb-10">
            Deliver measurable results, strengthen controls, and build repeatable tools.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="group inline-flex items-center px-10 py-5 rounded-xl font-semibold text-lg bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 transition-all duration-300 shadow-lg shadow-cyan-500/25"
          >
            Start a Conversation
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default AboutPage;
